import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type ToggleButtonSize = 'small' | 'medium' | 'large';

/**
 * uwc-togglebutton
 *
 * Production-grade toggle button component with W3C form association and
 * constraint validation. Set `required` to enforce the button must be on.
 *
 * @attr checked   - Whether the button is in the "on" state.
 * @attr on-label  - Label shown when checked. Default: Yes.
 * @attr off-label - Label shown when unchecked. Default: No.
 * @attr on-icon   - Icon name shown when checked.
 * @attr off-icon  - Icon name shown when unchecked.
 * @attr size      - small | medium | large. Default: medium.
 * @attr outline   - Show outlined style when unchecked.
 * @attr required  - The button must be in the on state.
 * @attr disabled  - Disable the toggle button.
 * @attr name      - Name for form submission.
 *
 * @fires uwc-change  - Fired when checked state changes. Detail: { checked }.
 * @fires uwc-invalid - Fired when required but off. Detail: { validity, validationMessage }.
 */
export class UwcToggleButton extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) checked  = false;
  @property({ attribute: 'on-label' })        onLabel  = 'Yes';
  @property({ attribute: 'off-label' })       offLabel = 'No';
  @property({ attribute: 'on-icon' })         onIcon?:  string;
  @property({ attribute: 'off-icon' })        offIcon?: string;
  @property()                                 name?:    string;

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ reflect: true })                size: ToggleButtonSize = 'medium';
  @property({ type: Boolean, reflect: true }) outline  = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;

  override firstUpdated() {
    const input = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(input);
    this._validation.syncFormValue(this.checked ? 'on' : null);
    this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.checked = input.checked;
    this._spawnRipple(e as unknown as MouseEvent);
    this._validation.handleChange(this.checked ? 'on' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(this.checked ? 'on' : null);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { checked: this.checked, originalEvent: e },
    }));
  }

  private _spawnRipple(e: MouseEvent) {
    const btn = this.shadowRoot?.querySelector<HTMLElement>('[part="button"]');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x    = (e.clientX || rect.left + rect.width / 2)  - rect.left - size / 2;
    const y    = (e.clientY || rect.top  + rect.height / 2) - rect.top  - size / 2;
    const span = document.createElement('span');
    span.className = 'uwc-tb-ripple';
    span.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`;
    btn.appendChild(span);
    span.addEventListener('animationend', () => span.remove(), { once: true });
  }

  // ── Render ────────────────────────────────────────────────────────

  private _renderIcon(): TemplateResult | typeof nothing {
    const iconName = this.checked ? this.onIcon : this.offIcon;
    if (!iconName) return nothing;
    return html`<uwc-icon class="uwc-tb__icon" name=${iconName} size="1em"></uwc-icon>`;
  }

  override render() {
    const label = this.checked ? this.onLabel : this.offLabel;

    return html`
      <label part="button" class="uwc-tb" aria-pressed=${this.checked}>
        <input
          type="checkbox"
          .checked=${this.checked}
          name=${ifDefined(this.name)}
          ?disabled=${this.disabled}
          ?required=${this.required}
          @change=${this._onChange}
        />
        ${this._renderIcon()}
        <span class="uwc-tb__label">${label}</span>
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-togglebutton': UwcToggleButton; }
}
